import { useTheme } from 'next-themes';
import { type Density, useDensity } from '@/components/layout/DensityProvider';
import { Section, SectionHead, Surface } from '@/components/ui/section';
import { Segmented, SettingRow } from '@/components/ui/segmented';

/** The two densities, and what each one does to a page. */
const DENSITIES: { value: Density; label: string }[] = [
  { value: 'roomy', label: 'Roomy' },
  { value: 'compact', label: 'Compact' },
];

type ThemeChoice = 'light' | 'dark' | 'system';
const THEMES: { value: ThemeChoice; label: string }[] = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
];

/** This console: the preferences that are genuinely yours, theme and density. */
export function PreferencesSection() {
  const { density, setDensity } = useDensity();
  // next-themes stores `system` as its own value rather than resolving it away,
  // which is exactly the three-way choice this control offers.
  const { theme, setTheme } = useTheme();
  const current: ThemeChoice =
    theme === 'light' || theme === 'dark' || theme === 'system' ? theme : 'system';
  return (
    <Section>
      <SectionHead title="This console" meta="stored in this browser, not on the hub" />
      <Surface>
        <div className="divide-line divide-y">
          <SettingRow
            title="Theme"
            hint="Both are designed rather than one inverted. System follows the machine until you pick."
            control={
              <Segmented
                label="Theme"
                value={current}
                options={THEMES}
                onChange={(next) => setTheme(next)}
              />
            }
          />
          <SettingRow
            title="Density"
            hint={
              <>
                Compact tightens every row and gap. Press <kbd className="dy-kbd">d</kbd> anywhere
                to switch.
              </>
            }
            control={
              <Segmented
                label="Row density"
                value={density}
                options={DENSITIES}
                onChange={setDensity}
              />
            }
          />
        </div>
      </Surface>
    </Section>
  );
}
